import { PageView, cn } from "@ceedero/ui";

import { PROJECT_COVER_TRANSITION_TYPE } from "@/navigation/constants/transition-types";
import { useArrivedWithTransitionType } from "@/navigation/hooks/use-arrived-with-transition-type";

import type { ComponentProps } from "react";

type PageWidth = NonNullable<ComponentProps<typeof PageView>["width"]>;

/**
 * How wide each page's column may grow, centred in the window.
 *
 * `@ceedero/ui` sets a page against the left edge, because on the desktop it
 * stands beside a navigation rail and the rail, the title and the content share
 * that edge. Here the pages sit under a bar across the top, and a column pinned
 * left left most of a wide window empty on one side — so this application
 * centres every page, and caps `full` at the measure the bar above it is set to,
 * rather than letting a catalog run the width of a 4K screen.
 */
const PAGE_MEASURE_CLASS_NAMES: Record<PageWidth, string> = {
  narrow: "max-w-2xl",
  medium: "max-w-4xl",
  wide: "max-w-5xl",
  extraWide: "max-w-6xl",
  full: "max-w-[90rem]",
};

/**
 * The measure of the bar across the top of every page, which the widest page
 * matches so the brand and the page beneath it start at the same edge.
 */
export const APP_SHELL_MEASURE_CLASS_NAMES = "mx-auto w-full max-w-[90rem] px-6 lg:px-8";

/**
 * A full page of this application.
 *
 * On a phone the document itself scrolls, so the browser's own chrome can
 * collapse; from `lg` the page is the scroll container under the fixed bar, and
 * its header stays pinned above its content.
 *
 * The header is centred on the same column as the content. The band it sits in
 * is padded by the package, so the column cancels that padding and carries its
 * own — otherwise a title would start one gutter to the right of the content
 * under it whenever the window is narrower than the column.
 *
 * A page a cover is travelling into does not play its entrance. The entrance
 * rises the whole page, cover included, and the browser measures where the
 * cover lands while the page is still below its resting place. Chrome follows
 * the page up; Firefox keeps the first measurement, so the cover landed half a
 * rem low and jumped when the transition let go of it.
 */
export const AppPage = ({
  width = "full",
  header,
  fills = false,
  className,
  contentClassName,
  entersWithAnimation = true,
  scrollContentClassName,
  children,
  ...props
}: ComponentProps<typeof PageView> & {
  /**
   * The page is one surface filling everything under its header — the Shelf
   * board — rather than a column that scrolls. Its content is placed edge to
   * edge and is given the height to fill, which a scrolling page cannot give it.
   */
  fills?: boolean;
}) => {
  const arrivedWithCover = useArrivedWithTransitionType(PROJECT_COVER_TRANSITION_TYPE);
  const playsEntrance = entersWithAnimation && !arrivedWithCover;
  const measure = cn("mx-auto w-full", PAGE_MEASURE_CLASS_NAMES[width ?? "full"]);
  const centredHeader = header && (
    <div className="-mx-6 lg:-mx-8">
      <div className={cn(measure, "px-6 lg:px-8")}>{header}</div>
    </div>
  );
  if (fills) {
    // The package's page frame, minus its scroll area: the same pinned header
    // band, over a region that fills the rest of the window. On a phone the
    // document scrolls, so the region takes most of the screen instead.
    return (
      <main
        className={cn(
          "flex flex-col bg-background lg:h-full lg:min-h-0",
          playsEntrance && "animate-view-in",
          className,
        )}
        {...props}
      >
        {centredHeader && (
          <div className="shrink-0 border-b border-border bg-card px-6 py-4 lg:px-8">
            {centredHeader}
          </div>
        )}
        <div className={cn("relative h-[75dvh] lg:h-auto lg:min-h-0 lg:flex-1", contentClassName)}>
          {children}
        </div>
      </main>
    );
  }
  return (
    <PageView
      width={width}
      className={cn("h-auto lg:h-full", className)}
      contentClassName={cn(measure, contentClassName)}
      entersWithAnimation={playsEntrance}
      {...(scrollContentClassName === undefined ? {} : { scrollContentClassName })}
      header={centredHeader}
      {...props}
    >
      {children}
    </PageView>
  );
};
